import React, { useCallback, memo } from 'react';

const Textarea = (props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea {...props} className={"w-full min-h-[72px] px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 " + (props.className || "")} />
);

interface InterviewQuestion {
  id: string;
  text: string;
}

interface InterviewQuestionsSectionProps {
  questions: InterviewQuestion[];
  onChange: (questions: InterviewQuestion[]) => void;
}

function InterviewQuestionsSection({ questions, onChange }: InterviewQuestionsSectionProps) {
  const updateQuestion = useCallback((id: string, value: string) => {
    onChange(questions.map(q => 
      q.id === id ? { ...q, text: value } : q
    ));
  }, [questions, onChange]);

  const addQuestion = useCallback(() => {
    const newId = `iq-${Date.now()}`;
    onChange([...questions, { id: newId, text: '' }]);
  }, [questions, onChange]);

  const removeQuestion = useCallback((id: string) => {
    if (questions.length > 1) {
      onChange(questions.filter(q => q.id !== id));
    }
  }, [questions, onChange]);

  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm">
      <h3 className="text-base font-semibold text-gray-900 mb-4">Interview Questions</h3>
      <div className="space-y-3">
        {questions.map((question, index) => (
          <div key={question.id} className="flex gap-2 items-start">
            <div className="flex-shrink-0 text-sm text-gray-600 mt-2 w-6">{index + 1}.</div>
            <Textarea
              placeholder="Enter interview question..."
              rows={2}
              value={question.text}
              onChange={(e) => updateQuestion(question.id, e.target.value)}
              className="flex-1"
            />
            {questions.length > 1 && (
              <button
                onClick={() => removeQuestion(question.id)}
                className="flex-shrink-0 text-red-600 hover:text-red-800 text-sm mt-2"
                title="Remove question"
              >
                ×
              </button>
            )}
          </div>
        ))}
        <button
          onClick={addQuestion}
          className="text-sm text-blue-600 hover:underline"
        >
          + Add Interview Question
        </button>
      </div>
    </div>
  );
}

// Memoize to prevent re-renders when parent re-renders but props haven't changed
export default memo(InterviewQuestionsSection);

